// react
import { useMemo } from 'react';

// components
import useStableSearchParams from '@/hooks/useStableSearchParams';

// relative
import type { DateRange } from '../types';

import { DateTime } from 'luxon';

function getDateRangeWithDefaults(
  from: string | null,
  to: string | null,
): DateRange {
  if (!from || !to) {
    const now = DateTime.now();
    return { from: now.minus({ days: 3 }), to: now };
  }
  return { from: DateTime.fromISO(from), to: DateTime.fromISO(to) };
}

function useDateRange(): [DateRange, (range: DateRange) => void] {
  const [searchParams, setSearchParams] = useStableSearchParams();

  const fromStr = searchParams.get('from');
  const toStr = searchParams.get('to');

  // Memoize by ISO strings so DateTime identity stays stable across re-renders.
  // Without this, new DateTime objects every render cause all dateRange.from/to
  // deps (useMemo, useEffect) to fire on every render regardless of actual date change.
  const dateRange = useMemo(
    () => getDateRangeWithDefaults(fromStr, toStr),
    [fromStr, toStr],
  );

  const setDateRangeInUrl = (range: DateRange) => {
    setSearchParams((prev: URLSearchParams) => {
      const nextParams = new URLSearchParams(prev);
      if (range.from) nextParams.set('from', range.from.toISO() ?? '');
      if (range.to) nextParams.set('to', range.to.toISO() ?? '');
      return nextParams;
    });
  };

  return [dateRange, setDateRangeInUrl];
}

export default useDateRange;
